import { useState, useEffect } from 'react';
import { getPositionsForSport } from '@/data/sportsPositions';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { teamService } from '@/services/teamService';
import { Search, Plus, X, User, Shield, Users, Upload, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useInternalAuth } from '@/hooks/useInternalAuth';

interface User {
  id: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  user_type: 'supabase' | 'internal';
  profile_photo_url?: string;
  slug?: string;
}

interface TeamConnection {
  id: string;
  user_id: string;
  connection_type: 'player' | 'coach' | 'support_staff';
  role?: string;
  position?: string;
  user?: User;
}

interface TeamPhoto {
  id: string;
  photo_url: string;
  photo_caption?: string;
  display_order: number;
}

interface Team {
  team_id: number;
  team_name: string;
  sport_type?: string;
  team_logo?: string;
}

interface UnifiedTeamManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  team: Team;
  onUpdate: () => void;
}

const COACH_ROLES = [
  'head_coach',
  'assistant_coach',
  'goalkeeping_coach', 
  'fitness_coach',
  'tactical_coach',
  'youth_coach',
  'volunteer_coach'
];

const SUPPORT_ROLES = [
  'team_manager',
  'physiotherapist',
  'nutritionist',
  'psychologist',
  'equipment_manager',
  'video_analyst',
  'scout',
  'media_manager'
];

const getSportPositions = (sport?: string): string[] => {
  if (!sport) return ['Player'];
  return getPositionsForSport(sport);
};

const UnifiedTeamManagementModal = ({
  isOpen,
  onClose,
  team,
  onUpdate
}: UnifiedTeamManagementModalProps) => {
  const navigate = useNavigate();
  const { user: internalUser } = useInternalAuth();
  const [activeTab, setActiveTab] = useState('logo');
  const [searchTerm, setSearchTerm] = useState('');
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedPosition, setSelectedPosition] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Team connections state
  const [players, setPlayers] = useState<TeamConnection[]>([]);
  const [coaches, setCoaches] = useState<TeamConnection[]>([]);
  const [supportStaff, setSupportStaff] = useState<TeamConnection[]>([]);
  
  // Team photos state
  const [photos, setPhotos] = useState<TeamPhoto[]>([]);
  const [newPhotoUrl, setNewPhotoUrl] = useState('');
  const [newPhotoCaption, setNewPhotoCaption] = useState('');

  // Logo upload state
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (isOpen && team.team_id) {
      loadTeamData();
    }
  }, [isOpen, team.team_id]);

  const loadTeamData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadPlayers(),
        loadCoaches(), 
        loadSupportStaff(),
        loadPhotos()
      ]);
    } catch (error) {
      console.error('Error loading team data:', error);
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const loadPlayers = async () => {
    try {
      const { data: teamPlayers, error } = await supabase
        .from('team_players')
        .select('*')
        .eq('team_id', team.team_id);

      if (error) throw error;

      const playersWithDetails = await Promise.all(
        (teamPlayers || []).map(async (player) => {
          const userDetails = await getUserDetails(player.user_id);
          return {
            id: `${player.user_id}-${team.team_id}`,
            user_id: player.user_id,
            connection_type: 'player' as const,
            position: player.position,
            user: userDetails
          };
        })
      );

      setPlayers(playersWithDetails);
    } catch (error) {
      console.error('Error loading players:', error);
    }
  };

  const loadCoaches = async () => {
    try {
      const { data: teamCoaches, error } = await supabase
        .from('team_coaches')
        .select('*')
        .eq('team_id', team.team_id);

      if (error) throw error;

      const coachesWithDetails = await Promise.all(
        (teamCoaches || []).map(async (coach) => {
          const userDetails = await getUserDetails(coach.coach_id);
          return {
            id: `${coach.coach_id}-${team.team_id}`,
            user_id: coach.coach_id,
            connection_type: 'coach' as const,
            role: coach.role,
            user: userDetails
          };
        })
      );

      setCoaches(coachesWithDetails);
    } catch (error) {
      console.error('Error loading coaches:', error);
    }
  };

  const loadSupportStaff = async () => {
    try {
      const { data: supportConnections, error } = await supabase
        .from('team_connections')
        .select('*')
        .eq('team_id', team.team_id)
        .eq('connection_type', 'support');

      if (error) throw error;

      const supportWithDetails = await Promise.all(
        (supportConnections || []).map(async (support) => {
          const userDetails = await getUserDetails(support.user_id);
          return {
            id: support.id,
            user_id: support.user_id,
            connection_type: 'support_staff' as const,
            role: support.role,
            user: userDetails
          };
        })
      );

      setSupportStaff(supportWithDetails);
    } catch (error) {
      console.error('Error loading support staff:', error);
    }
  };

  const loadPhotos = async () => {
    try {
      const teamPhotos = await teamService.getTeamPhotos(team.team_id);
      setPhotos(teamPhotos);
    } catch (error) {
      console.error('Error loading photos:', error);
    }
  };

  const getUserDetails = async (userId: string): Promise<User> => {
    try {
      // Try internal users first for better under-13 user support
      const { data: internalUser } = await supabase
        .from('internal_users')
        .select('username, role, age_group')
        .eq('id', userId)
        .maybeSingle();

      if (internalUser) {
        // Get profile data including age information
        const { data: profile } = await supabase
          .from('internal_user_profiles')
          .select('age, profile_photo_url')
          .eq('user_id', userId)
          .maybeSingle();

        return {
          id: userId,
          username: internalUser.username,
          role: internalUser.role,
          profile_photo_url: profile?.profile_photo_url || null,
          user_type: 'internal'
        };
      }

      // Fallback to Supabase profiles
      const { data: profile } = await supabase
        .from('profiles')
        .select('username, first_name, last_name, profile_photo_url, role, slug')
        .eq('id', userId)
        .single();

      if (profile) {
        return {
          id: userId,
          username: profile.username,
          first_name: profile.first_name,
          last_name: profile.last_name,
          profile_photo_url: profile.profile_photo_url,
          role: profile.role,
          slug: profile.slug,
          user_type: 'supabase'
        };
      }

      return {
        id: userId,
        username: 'Unknown User',
        user_type: 'supabase'
      };
    } catch (error) {
      console.error('Error fetching user details:', error);
      return {
        id: userId,
        username: 'Unknown User',
        user_type: 'supabase'
      };
    }
  };

  const searchUsers = async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setAvailableUsers([]);
      return;
    }

    try {
      const users = await teamService.searchUsers(searchTerm);
      setAvailableUsers(users);
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error('Failed to search users');
    }
  };

  const handleUserSearch = (value: string) => {
    setSearchTerm(value);
    if (value.length >= 2) {
      searchUsers(value);
    } else {
      setAvailableUsers([]);
    }
  };

  const addPlayer = async (user: User) => {
    if (!selectedPosition) {
      toast.error('Please select a position');
      return;
    }

    try {
      await teamService.addPlayerToTeam(team.team_id, user.id, selectedPosition);
      toast.success('Player added successfully');
      await loadPlayers();
      setSearchTerm('');
      setSelectedPosition('');
      setAvailableUsers([]);
    } catch (error) {
      console.error('Error adding player:', error);
      toast.error('Failed to add player');
    }
  };

  const addCoach = async (user: User) => {
    if (!selectedRole) {
      toast.error('Please select a coaching role');
      return;
    }

    try {
      await teamService.addCoachToTeam(team.team_id, user.id, selectedRole);
      toast.success('Coach added successfully');
      await loadCoaches();
      setSearchTerm('');
      setSelectedRole('');
      setAvailableUsers([]);
    } catch (error) {
      console.error('Error adding coach:', error);
      toast.error('Failed to add coach');
    }
  };

  const addSupportStaff = async (user: User) => {
    if (!selectedRole) {
      toast.error('Please select a support role');
      return;
    }

    try {
      const { error } = await supabase
        .from('team_connections')
        .insert({
          team_id: team.team_id,
          user_id: user.id,
          connection_type: 'support',
          role: selectedRole
        });

      if (error) throw error;

      toast.success('Support staff added successfully');
      await loadSupportStaff();
      setSearchTerm('');
      setSelectedRole('');
      setAvailableUsers([]);
    } catch (error) {
      console.error('Error adding support staff:', error);
      toast.error('Failed to add support staff');
    }
  };

  const removePlayer = async (userId: string) => {
    try {
      await teamService.removePlayerFromTeam(team.team_id, userId);
      toast.success('Player removed successfully');
      await loadPlayers();
    } catch (error) {
      console.error('Error removing player:', error);
      toast.error('Failed to remove player');
    }
  };

  const removeCoach = async (userId: string) => {
    try {
      await teamService.removeCoachFromTeam(team.team_id, userId);
      toast.success('Coach removed successfully');
      await loadCoaches();
    } catch (error) {
      console.error('Error removing coach:', error);
      toast.error('Failed to remove coach');
    }
  };

  const removeSupportStaff = async (connectionId: string) => {
    try {
      const { error } = await supabase
        .from('team_connections')
        .delete()
        .eq('id', connectionId);

      if (error) throw error;

      toast.success('Support staff removed successfully');
      await loadSupportStaff();
    } catch (error) {
      console.error('Error removing support staff:', error);
      toast.error('Failed to remove support staff');
    }
  };

  const addPhoto = async () => {
    if (!newPhotoUrl.trim()) {
      toast.error('Please enter a photo URL');
      return;
    }

    try {
      console.log('Adding photo for team:', team.team_id, 'URL:', newPhotoUrl, 'Caption:', newPhotoCaption);
      await teamService.addTeamPhoto(team.team_id, newPhotoUrl, newPhotoCaption);
      toast.success('Photo added successfully');
      await loadPhotos();
      setNewPhotoUrl('');
      setNewPhotoCaption('');
    } catch (error) {
      console.error('Error adding photo:', error);
      toast.error('Failed to add photo');
    }
  };

  const removePhoto = async (photoId: string) => {
    try {
      await teamService.removeTeamPhoto(photoId);
      toast.success('Photo removed successfully');
      await loadPhotos();
    } catch (error) {
      console.error('Error removing photo:', error);
      toast.error('Failed to remove photo');
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) {
      toast.error('Please select a logo file');
      return;
    }

    try {
      setUploading(true);
      
      // Get current user from both auth sources
      const { data: { session } } = await supabase.auth.getSession();
      const { data: { user } } = await supabase.auth.getUser();
      
      // Use the appropriate user ID - prioritize Supabase auth, fallback to internal
      const userId = user?.id || internalUser?.id;
      if (!userId) {
        console.error('No valid user ID found');
        toast.error('User not authenticated');
        return;
      }
      
      console.log('Uploading logo for user:', userId);

      // Create file name with team ID and user folder
      const fileExt = logoFile.name.split('.').pop();
      const fileName = `team-logo-${team.team_id}.${fileExt}`;
      const filePath = `${userId}/${fileName}`;
      
      // Upload to team-assets bucket
      const { data, error } = await supabase.storage
        .from('team-assets')
        .upload(filePath, logoFile, { 
          cacheControl: '3600',
          upsert: true 
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('team-assets')
        .getPublicUrl(filePath);

      // Update team logo in database
      await teamService.updateTeam(team.team_id, { team_logo: publicUrl });

      toast.success('Logo uploaded successfully');
      onUpdate();
      setLogoFile(null);
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const navigateToProfile = (user: User) => {
    // Use slug or username for profile navigation
    const profileIdentifier = user.slug || user.username;
    
    if (profileIdentifier) {
      navigate(`/${profileIdentifier}`);
    } else {
      // Fallback for users without slug/username
      console.warn('No profile identifier found for user:', user);
    }
    onClose();
  };

  const renderUserCard = (connection: TeamConnection, onRemove: () => void) => {
    const user = connection.user;
    if (!user) return null;

    return (
      <div key={connection.id} className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Avatar className="h-12 w-12">
              <AvatarImage src={user.profile_photo_url} />
              <AvatarFallback>
                <User className="h-6 w-6" />
              </AvatarFallback>
            </Avatar>
            {/* Profile navigation arrow */}
            <button
              onClick={() => navigateToProfile(user)}
              className="absolute -top-1 -right-1 w-6 h-6 bg-sports-green hover:bg-sports-green/80 rounded-sm flex items-center justify-center transition-colors"
            >
              <ArrowRight className="h-3 w-3 text-black" />
            </button>
          </div>
          <div>
            <div className="font-medium text-white">
              {user.username || `${user.first_name} ${user.last_name}`.trim() || 'Unknown User'}
            </div>
            <div className="text-sm text-muted-foreground">
              {connection.position || connection.role || 'Position'}
            </div>
          </div>
        </div>
        <Button
          size="sm"
          variant="destructive"
          onClick={onRemove}
          className="h-8 w-8 p-0"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Manage {team.team_name}</DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="logo" className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              Logo
            </TabsTrigger>
            <TabsTrigger value="players" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Players
            </TabsTrigger>
            <TabsTrigger value="coaches" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Coaches
            </TabsTrigger>
            <TabsTrigger value="support" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Support
            </TabsTrigger>
            <TabsTrigger value="photos">Photos</TabsTrigger>
            <TabsTrigger value="details">Details</TabsTrigger>
          </TabsList>

          <div className="overflow-y-auto flex-1 mt-4">
            {/* Logo Tab */}
            <TabsContent value="logo" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Team Logo</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {team.team_logo && (
                    <div className="text-center">
                      <img
                        src={team.team_logo}
                        alt="Current team logo"
                        className="w-32 h-32 object-contain mx-auto mb-4 border rounded-lg"
                      />
                      <p className="text-sm text-muted-foreground">Current Logo</p>
                    </div>
                  )}
                  
                  <div>
                    <Label>Upload New Logo</Label>
                    <Input
                      type="file"
                      accept=".jpeg,.png,.jpg,image/jpeg,image/png"
                      onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                      className="mt-2"
                    />
                  </div>
                  
                  <Button
                    onClick={handleLogoUpload}
                    disabled={!logoFile || uploading}
                    className="w-full bg-sports-green text-black hover:bg-sports-green/80"
                  >
                    {uploading ? 'Uploading...' : 'Upload Logo'}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Players Tab */}
            <TabsContent value="players" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Add Players</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Search Players</Label>
                      <div className="relative">
                        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search by username or name..."
                          value={searchTerm}
                          onChange={(e) => handleUserSearch(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Position</Label>
                      <Select value={selectedPosition} onValueChange={setSelectedPosition}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select position" />
                        </SelectTrigger>
                        <SelectContent>
                          {getSportPositions(team.sport_type).map((position) => (
                            <SelectItem key={position} value={position}>
                              {position}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {availableUsers.length > 0 && (
                    <div className="space-y-2">
                      <Label>Available Users</Label>
                      <div className="max-h-32 overflow-y-auto space-y-2">
                        {availableUsers.map((user) => (
                          <div key={user.id} className="flex items-center justify-between p-2 bg-muted/20 rounded">
                            <span>{user.username || `${user.first_name} ${user.last_name}`.trim()}</span>
                            <Button
                              size="sm"
                              onClick={() => addPlayer(user)}
                              className="bg-sports-green text-black hover:bg-sports-green/80"
                            >
                              Add
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Current Players ({players.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {players.length > 0 ? (
                    <div className="space-y-3">
                      {players.map((player) => renderUserCard(player, () => removePlayer(player.user_id)))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-4">No players added yet.</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Coaches Tab */}
            <TabsContent value="coaches" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Add Coaches</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Search Coaches</Label>
                      <div className="relative">
                        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search by username or name..."
                          value={searchTerm}
                          onChange={(e) => handleUserSearch(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Coaching Role</Label>
                      <Select value={selectedRole} onValueChange={setSelectedRole}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select coaching role" />
                        </SelectTrigger>
                        <SelectContent>
                          {COACH_ROLES.map((role) => (
                            <SelectItem key={role} value={role}>
                              {role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {availableUsers.length > 0 && (
                    <div className="space-y-2">
                      <Label>Available Users</Label>
                      <div className="max-h-32 overflow-y-auto space-y-2">
                        {availableUsers.map((user) => (
                          <div key={user.id} className="flex items-center justify-between p-2 bg-muted/20 rounded">
                            <span>{user.username || `${user.first_name} ${user.last_name}`.trim()}</span>
                            <Button
                              size="sm"
                              onClick={() => addCoach(user)}
                              className="bg-sports-green text-black hover:bg-sports-green/80"
                            >
                              Add
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Current Coaches ({coaches.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {coaches.length > 0 ? (
                    <div className="space-y-3">
                      {coaches.map((coach) => renderUserCard(coach, () => removeCoach(coach.user_id)))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-4">No coaches added yet.</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Support Tab */}
            <TabsContent value="support" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Add Support Staff</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Search Support Staff</Label>
                      <div className="relative">
                        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search by username or name..."
                          value={searchTerm}
                          onChange={(e) => handleUserSearch(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Support Role</Label>
                      <Select value={selectedRole} onValueChange={setSelectedRole}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select support role" />
                        </SelectTrigger>
                        <SelectContent>
                          {SUPPORT_ROLES.map((role) => (
                            <SelectItem key={role} value={role}>
                              {role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {availableUsers.length > 0 && (
                    <div className="space-y-2">
                      <Label>Available Users</Label>
                      <div className="max-h-32 overflow-y-auto space-y-2">
                        {availableUsers.map((user) => (
                          <div key={user.id} className="flex items-center justify-between p-2 bg-muted/20 rounded">
                            <span>{user.username || `${user.first_name} ${user.last_name}`.trim()}</span>
                            <Button
                              size="sm"
                              onClick={() => addSupportStaff(user)}
                              className="bg-sports-green text-black hover:bg-sports-green/80"
                            >
                              Add
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Current Support Staff ({supportStaff.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {supportStaff.length > 0 ? (
                    <div className="space-y-3">
                      {supportStaff.map((staff) => renderUserCard(staff, () => removeSupportStaff(staff.id)))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-4">No support staff added yet.</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Photos Tab */}
            <TabsContent value="photos" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Photo Upload</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Photo URL</Label>
                    <Input
                      type="url"
                      placeholder="Enter photo URL..."
                      value={newPhotoUrl}
                      onChange={(e) => setNewPhotoUrl(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Supported file types: .jpeg, .png, .jpeg/image, .png/image
                    </p>
                  </div>
                  <div>
                    <Label>Caption (Optional)</Label>
                    <Input
                      placeholder="Enter photo caption..."
                      value={newPhotoCaption}
                      onChange={(e) => setNewPhotoCaption(e.target.value)}
                    />
                  </div>
                  <Button
                    onClick={addPhoto}
                    className="w-full bg-sports-green text-black hover:bg-sports-green/80"
                  >
                    Add Photo
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Team Photos ({photos.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {photos.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {photos.map((photo) => (
                        <div key={photo.id} className="relative group">
                          <img
                            src={photo.photo_url}
                            alt={photo.photo_caption || 'Team photo'}
                            className="w-full h-32 object-cover rounded-lg"
                          />
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => removePhoto(photo.id)}
                            className="absolute top-2 right-2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                          {photo.photo_caption && (
                            <p className="text-xs text-muted-foreground mt-1 truncate">
                              {photo.photo_caption}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-4">No photos added yet.</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Details Tab */}
            <TabsContent value="details" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Team Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Team Name</Label>
                    <Input
                      value={team.team_name}
                      readOnly
                      className="bg-muted/20"
                    />
                  </div>
                  <div>
                    <Label>Sport Type</Label>
                    <Input
                      value={team.sport_type || 'Not specified'}
                      readOnly
                      className="bg-muted/20"
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    To edit team details, contact your team administrator.
                  </p>
                </CardContent>
              </Card>
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default UnifiedTeamManagementModal;